import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import BookingWidget from '../components/BookingWidget';

const Policies = () => {
  const { t } = useLanguage();

  return (
    <div data-testid="policies-page" className="min-h-screen pt-24 pb-32 bg-white">
      {/* Header */}
      <div className="max-w-4xl mx-auto px-6 md:px-12 py-12 md:py-16">
        <p className="uppercase tracking-widest text-xs text-[#D4A72C] mb-4">
          {t('nav.policies')}
        </p>
        <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-[#1E3A5F] mb-6">
          {t('policies.title')}
        </h1>
        <p className="text-muted-foreground text-lg">
          {t('policies.subtitle')}
        </p>
      </div>

      <div className="max-w-4xl mx-auto px-6 md:px-12 space-y-16">
        {/* House Rules */}
        <section id="rules" data-testid="rules-section">
          <h2 className="font-serif text-2xl md:text-3xl text-[#1E3A5F] mb-6 pb-4 border-b border-[#1E3A5F]/10">
            {t('rules.title')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-[#f8fafc] p-4 rounded-sm border border-[#1E3A5F]/10">
              <p className="font-semibold text-[#1E3A5F] mb-1">{t('rules.checkInLabel')}</p>
              <p className="text-[#1E3A5F]/70 text-sm">{t('rules.checkInTime')}</p>
            </div>
            <div className="bg-[#f8fafc] p-4 rounded-sm border border-[#1E3A5F]/10">
              <p className="font-semibold text-[#1E3A5F] mb-1">{t('rules.checkOutLabel')}</p>
              <p className="text-[#1E3A5F]/70 text-sm">{t('rules.checkOutTime')}</p>
            </div>
            <div className="bg-[#f8fafc] p-4 rounded-sm border border-[#1E3A5F]/10">
              <p className="font-semibold text-[#1E3A5F] mb-1">{t('rules.childrenLabel')}</p>
              <p className="text-[#1E3A5F]/70 text-sm">{t('rules.childrenText')}</p>
            </div>
            <div className="bg-[#f8fafc] p-4 rounded-sm border border-[#1E3A5F]/10">
              <p className="font-semibold text-[#1E3A5F] mb-1">{t('rules.cribsLabel')}</p>
              <p className="text-[#1E3A5F]/70 text-sm">{t('rules.cribsText')}</p>
            </div>
            <div className="bg-[#f8fafc] p-4 rounded-sm border border-[#1E3A5F]/10">
              <p className="font-semibold text-[#1E3A5F] mb-1">{t('rules.smokingLabel')}</p>
              <p className="text-[#1E3A5F]/70 text-sm">{t('rules.smokingText')}</p>
            </div>
            <div className="bg-[#f8fafc] p-4 rounded-sm border border-[#1E3A5F]/10">
              <p className="font-semibold text-[#1E3A5F] mb-1">{t('rules.partiesLabel')}</p>
              <p className="text-[#1E3A5F]/70 text-sm">{t('rules.partiesText')}</p>
            </div>
            <div className="bg-[#f8fafc] p-4 rounded-sm border border-[#1E3A5F]/10">
              <p className="font-semibold text-[#1E3A5F] mb-1">{t('rules.quietHoursLabel')}</p>
              <p className="text-[#1E3A5F]/70 text-sm">{t('rules.quietHoursTime')}</p>
            </div>
            <div className="bg-[#f8fafc] p-4 rounded-sm border border-[#1E3A5F]/10">
              <p className="font-semibold text-[#1E3A5F] mb-1">{t('rules.petsLabel')}</p>
              <p className="text-[#1E3A5F]/70 text-sm">{t('rules.petsText')}</p>
            </div>
            <div className="bg-[#f8fafc] p-4 rounded-sm border border-[#1E3A5F]/10">
              <p className="font-semibold text-[#1E3A5F] mb-1">{t('rules.parkingLabel')}</p>
              <p className="text-[#1E3A5F]/70 text-sm">{t('rules.parkingText')}</p>
            </div>
          </div>
        </section>

        {/* Terms & Conditions */}
        <section id="terms" data-testid="terms-section">
          <h2 className="font-serif text-2xl md:text-3xl text-[#1E3A5F] mb-6 pb-4 border-b border-[#1E3A5F]/10">
            {t('policies.terms')}
          </h2>
          <div className="prose prose-lg text-muted-foreground max-w-none">
            <h3 className="text-[#1E3A5F] font-semibold mt-6 mb-3">{t('policies.bookingProcess')}</h3>
            <p>{t('policies.bookingProcessText')}</p>

            <h3 className="text-[#1E3A5F] font-semibold mt-6 mb-3">{t('policies.paymentTerms')}</h3>
            <p>{t('policies.paymentTermsText')}</p>
          </div>
        </section>

        {/* Cancellation Policy */}
        <section id="cancellation" data-testid="cancellation-section">
          <h2 className="font-serif text-2xl md:text-3xl text-[#1E3A5F] mb-6 pb-4 border-b border-[#1E3A5F]/10">
            {t('policies.cancellation')}
          </h2>
          <div className="prose prose-lg text-muted-foreground max-w-none">
            <p>{t('policies.cancellationText')}</p>

            <h3 className="text-[#1E3A5F] font-semibold mt-6 mb-3">{t('policies.modifications')}</h3>
            <p>{t('policies.modificationsText')}</p>
          </div>
        </section>

        {/* Privacy Policy */}
        <section id="privacy" data-testid="privacy-section">
          <h2 className="font-serif text-2xl md:text-3xl text-[#1E3A5F] mb-6 pb-4 border-b border-[#1E3A5F]/10">
            {t('policies.privacy')}
          </h2>
          <div className="prose prose-lg text-muted-foreground max-w-none">
            <p className="text-sm italic">{t('policies.privacyLastUpdated')}</p>

            <h3 className="text-[#1E3A5F] font-semibold mt-6 mb-3">{t('policies.privacyWhoWeAre')}</h3>
            <p>{t('policies.privacyWhoWeAreText')}</p>

            <h3 className="text-[#1E3A5F] font-semibold mt-6 mb-3">{t('policies.privacyWhatWeCollect')}</h3>
            <p>{t('policies.privacyWhatWeCollectText')}</p>

            <h3 className="text-[#1E3A5F] font-semibold mt-6 mb-3">{t('policies.privacyWhyWeCollect')}</h3>
            <p>{t('policies.privacyWhyWeCollectText')}</p>

            <h3 className="text-[#1E3A5F] font-semibold mt-6 mb-3">{t('policies.privacyWhoHasAccess')}</h3>
            <p>{t('policies.privacyWhoHasAccessText')}</p>

            <h3 className="text-[#1E3A5F] font-semibold mt-6 mb-3">{t('policies.privacyYourRights')}</h3>
            <p>{t('policies.privacyYourRightsText')}</p>

            <h3 className="text-[#1E3A5F] font-semibold mt-6 mb-3">{t('policies.privacyCookies')}</h3>
            <p>{t('policies.privacyCookiesText')}</p>
          </div>
        </section>
      </div>

      {/* Sticky Mobile Booking */}
      <BookingWidget variant="sticky" />
    </div>
  );
};

export default Policies;
